import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import cx from "classnames";
import { IDateRange } from "../../../types/generic";
import { AnalyticsDateShortcut } from "../../../types/analytics/AnalyticsSavedFilter";
import {
  AnalyticsDateMode,
  isCommonDateShortcut,
} from "../../../helpers/analytics/analyticsDateModeHelpers";

export type { AnalyticsDateMode };

export type AnalyticsDatesControlHandle = {
  /** Exact draft as an absolute range, or null if either bound is missing. */
  getExactDateRange(): IDateRange | null;
  /** Common radio draft, or null if none selected in the popover. */
  getCommonDraftPreset(): AnalyticsDateShortcut | null;
};

interface AnalyticsDatesControlProps {
  /** Live mode tab (chrome only until Done). */
  dateMode: AnalyticsDateMode;
  onDateModeChange(mode: AnalyticsDateMode): void;
  /** Staged date intent after Done — seeds Common draft when entering that tab. */
  stagedDateShortcut: AnalyticsDateShortcut;
  dateRange: IDateRange | null | undefined;
  /** Formatted absolute range shown under All activity (last staged window). */
  fitPreviewLabel?: string;
  /** Quiet hint when All activity found no data and fell back to Last 12 months. */
  emptyActivityHint?: boolean;
  /** Distinct from empty: resolve request failed; Retry re-runs without treating as no-data. */
  activityWindowError?: boolean;
  onRetryActivityWindow?(): void;
  /**
   * Exact mode: reports whether both from/to dates are filled in the draft.
   * Used to block Done/Apply while Exact is incomplete.
   */
  onExactDatesCompleteChange?(complete: boolean): void;
  /**
   * Common mode: reports whether a radio is selected in the draft.
   * Used to block Done while Common has no preset chosen.
   */
  onCommonDraftCompleteChange?(complete: boolean): void;
}

function toDateInputValue(value: Date | undefined): string {
  if (!value) return "";
  try {
    return format(new Date(value), "yyyy-MM-dd");
  } catch {
    return "";
  }
}

function parseDateInputValue(
  value: string,
  endOfDay: boolean,
): Date | undefined {
  if (!value) return undefined;
  const d = new Date(value + (endOfDay ? "T23:59:59" : "T00:00:00"));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function commonDraftSeed(
  committed: AnalyticsDateShortcut,
): AnalyticsDateShortcut | null {
  if (!isCommonDateShortcut(committed)) return null;
  // UI radios use LAST_YEAR for the legacy LAST_12_MONTHS wire value.
  if (committed === "LAST_12_MONTHS") return "LAST_YEAR";
  return committed;
}

const COMMON_PRESETS: {
  key: AnalyticsDateShortcut;
  labelKey: string;
  fallback: string;
}[] = [
  {
    key: "LAST_30_DAYS",
    labelKey: "Analytics.Filters.Last30Days",
    fallback: "Last 30 days",
  },
  {
    key: "LAST_3_MONTHS",
    labelKey: "Analytics.Filters.Last3Months",
    fallback: "Last 3 months",
  },
  {
    key: "LAST_6_MONTHS",
    labelKey: "Analytics.Filters.Last6Months",
    fallback: "Last 6 months",
  },
  {
    key: "LAST_YEAR",
    labelKey: "Analytics.Filters.Last12Months",
    fallback: "Last 12 months",
  },
  {
    key: "YEAR_TO_DATE",
    labelKey: "Analytics.Filters.YearToDate",
    fallback: "This year to date",
  },
  {
    key: "LAST_FULL_YEAR",
    labelKey: "Analytics.Filters.LastFullYear",
    fallback: "Last full year",
  },
  {
    key: "ALL_TIME",
    labelKey: "Analytics.Filters.AllTime",
    fallback: "All time",
  },
];

const AnalyticsDatesControl = forwardRef<
  AnalyticsDatesControlHandle,
  AnalyticsDatesControlProps
>(function AnalyticsDatesControl(
  {
    dateMode,
    onDateModeChange,
    stagedDateShortcut,
    dateRange,
    fitPreviewLabel,
    emptyActivityHint,
    activityWindowError,
    onRetryActivityWindow,
    onExactDatesCompleteChange,
    onCommonDraftCompleteChange,
  },
  ref,
) {
  const { t } = useTranslation();

  // Exact + Common drafts stay local until Done — nothing stages from chrome alone.
  const [exactStart, setExactStart] = useState("");
  const [exactEnd, setExactEnd] = useState("");
  const [exactAttempted, setExactAttempted] = useState(false);
  const [commonDraftPreset, setCommonDraftPreset] =
    useState<AnalyticsDateShortcut | null>(null);

  useEffect(() => {
    if (dateMode !== "pick") {
      setExactAttempted(false);
      onExactDatesCompleteChange?.(true);
      return;
    }
    const start = toDateInputValue(dateRange?.startDate);
    const end = toDateInputValue(dateRange?.endDate);
    setExactStart(start);
    setExactEnd(end);
    setExactAttempted(false);
    onExactDatesCompleteChange?.(!!start && !!end);
    // Seed once when entering Exact; do not reset while the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-seed on mode enter
  }, [dateMode]);

  useEffect(() => {
    if (dateMode !== "common") {
      onCommonDraftCompleteChange?.(true);
      return;
    }
    const seed = commonDraftSeed(stagedDateShortcut);
    setCommonDraftPreset(seed);
    onCommonDraftCompleteChange?.(seed != null);
    // Seed once when entering Common; radio changes update draft locally.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-seed on mode enter
  }, [dateMode]);

  useEffect(() => {
    if (dateMode !== "pick") return;
    onExactDatesCompleteChange?.(!!exactStart && !!exactEnd);
  }, [dateMode, exactStart, exactEnd, onExactDatesCompleteChange]);

  useEffect(() => {
    if (dateMode !== "common") return;
    onCommonDraftCompleteChange?.(commonDraftPreset != null);
  }, [dateMode, commonDraftPreset, onCommonDraftCompleteChange]);

  useImperativeHandle(
    ref,
    () => ({
      getExactDateRange: () => {
        const startDate = parseDateInputValue(exactStart, false);
        const endDate = parseDateInputValue(exactEnd, true);
        if (!startDate || !endDate) return null;
        return { startDate, endDate };
      },
      getCommonDraftPreset: () => commonDraftPreset,
    }),
    [exactStart, exactEnd, commonDraftPreset],
  );

  const isCommonChecked = (key: AnalyticsDateShortcut) => {
    if (dateMode !== "common") return false;
    return commonDraftPreset === key;
  };

  const exactIncomplete = !exactStart || !exactEnd;
  const showExactValidation =
    dateMode === "pick" && exactAttempted && exactIncomplete;

  return (
    <div>
      <div
        className="inline-flex w-full mb-3 rounded-md shadow-sm"
        role="group"
        aria-label={t("Analytics.Filters.DateModeGroup", "Date mode")}
      >
        {(
          [
            ["fit", t("Analytics.Filters.AllActivity", "All activity")],
            ["common", t("Analytics.Filters.Common", "Common")],
            ["pick", t("Analytics.Filters.Exact", "Exact")],
          ] as const
        ).map(([mode, label], index, modes) => {
          const isFirst = index === 0;
          const isLast = index === modes.length - 1;
          const isActive = dateMode === mode;
          return (
            <button
              key={mode}
              type="button"
              aria-pressed={isActive}
              className={cx(
                "flex-1 px-1 py-1.5 text-[11px] font-semibold border transition-colors",
                isFirst && "rounded-l-md",
                isLast && "rounded-r-md",
                !isFirst && "-ml-px",
                isActive
                  ? "relative z-10 border-gray-800 bg-gray-100 text-gray-900"
                  : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700",
              )}
              onClick={() => {
                // Mode chrome only — stage on Done.
                onDateModeChange(mode);
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {dateMode === "fit" && (
        <div className="border border-dashed border-gray-300 rounded-md p-2.5 text-sm bg-gray-50 space-y-1">
          <div>
            {t(
              "Analytics.Filters.AllActivityHint",
              "Covering {{range}} based on assignment and response dates",
              { range: fitPreviewLabel || "-" },
            )}
          </div>
          {activityWindowError && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-gray-700">
                {t(
                  "Analytics.Filters.AllActivityResolveError",
                  "Could not load activity dates for these journeys.",
                )}
              </div>
              {onRetryActivityWindow && (
                <button
                  type="button"
                  className="btn-secondary btn-small shrink-0 self-start sm:self-auto"
                  onClick={onRetryActivityWindow}
                >
                  {t("Common.TryAgain", "Try again")}
                </button>
              )}
            </div>
          )}
          {!activityWindowError && emptyActivityHint && (
            <div className="text-xs text-gray-500">
              {t(
                "Analytics.Filters.AllActivityEmptyHint",
                "No activity found for these journeys - showing Last 12 months instead.",
              )}
            </div>
          )}
        </div>
      )}

      {dateMode === "common" && (
        <div className="space-y-1 pl-2">
          {COMMON_PRESETS.map(({ key, labelKey, fallback }) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="analytics-common-date-preset"
                checked={isCommonChecked(key)}
                onChange={() => setCommonDraftPreset(key)}
              />
              {t(labelKey, fallback)}
            </label>
          ))}
        </div>
      )}

      {dateMode === "pick" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-semibold text-gray-600">
              {t("Analytics.Filters.DateFrom", "From")}
              <input
                type="date"
                className={cx(
                  "mt-1 w-full border rounded-md px-2 py-1.5 text-sm font-normal text-gray-900",
                  showExactValidation && !exactStart
                    ? "border-red-400"
                    : "border-gray-300",
                )}
                value={exactStart}
                max={exactEnd || undefined}
                onChange={(e) => {
                  setExactStart(e.target.value);
                  setExactAttempted(true);
                }}
                onBlur={() => setExactAttempted(true)}
              />
            </label>
            <label className="block text-xs font-semibold text-gray-600">
              {t("Analytics.Filters.DateTo", "To")}
              <input
                type="date"
                className={cx(
                  "mt-1 w-full border rounded-md px-2 py-1.5 text-sm font-normal text-gray-900",
                  showExactValidation && !exactEnd
                    ? "border-red-400"
                    : "border-gray-300",
                )}
                value={exactEnd}
                min={exactStart || undefined}
                onChange={(e) => {
                  setExactEnd(e.target.value);
                  setExactAttempted(true);
                }}
                onBlur={() => setExactAttempted(true)}
              />
            </label>
          </div>
          {showExactValidation && (
            <p className="text-xs text-red-600" role="alert">
              {t(
                "Analytics.Filters.ExactDatesRequired",
                "Choose both a from and to date.",
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

export default AnalyticsDatesControl;
